import { isCalendarDate } from '@dcp/shared/utils/business-date'
import type { TenantTables } from '@dcp/shared/types/tenant-database'
import type { ProjectionFormData } from '@/types/projections'
import {
  OUTFLOW_CATEGORY_NAMES,
  REPORT_CATEGORY_LABEL,
  isOutflowCategoryName,
  type ReportCategoryName,
} from '@/constants/report-categories'

// Pure rules of the "Manage projections" modal: cell scope, drafts, validation, diff, sign hints.

export type ProjectionRecord = TenantTables['projections']['Row']

/** A cell's report row: one category, or the "Projected Outflows" subtotal of the ten. */
export type ProjectionCellRow = { kind: 'category'; name: ReportCategoryName } | { kind: 'outflows' }

export interface ProjectionCellFacility {
  id: string
  name: string
}

/** The column a cell belongs to: one facility, or a pack subtotal summing its members. */
export type ProjectionCellTarget =
  | { kind: 'facility'; id: string; name: string }
  | {
      kind: 'pack'
      id: string
      name: string
      /** The pack's active facilities as the report listed them — the ones its subtotal sums. */
      members: readonly ProjectionCellFacility[]
    }

/**
 * One Cash Position cell: facility or pack × period × report row. `start`/`end`
 * are the inclusive ISO bounds the report RPC was queried with.
 */
export interface ProjectionCellScope {
  target: ProjectionCellTarget
  periodLabel: string
  start: string
  end: string
  row: ProjectionCellRow
}

/** The facilities a cell's target covers, in report order. */
export function cellFacilities(target: ProjectionCellTarget): readonly ProjectionCellFacility[] {
  return target.kind === 'pack' ? target.members : [{ id: target.id, name: target.name }]
}

/** The report column shape `projectionCellTarget` reads (see `CashPositionMultiPeriodColumn`). */
export interface ProjectionCellColumn {
  key: string
  kind: 'facility' | 'pack'
  packId: string | null
  name: string
}

/**
 * The target of a clicked report column. A pack's members are the facility
 * columns of the SAME report result that its subtotal sums — not a facility
 * catalog — so the modal lists exactly what the cell counted, even while a
 * filter change refetches behind placeholder data.
 */
export function projectionCellTarget(
  column: ProjectionCellColumn,
  columns: readonly ProjectionCellColumn[],
): ProjectionCellTarget {
  if (column.kind === 'facility') return { kind: 'facility', id: column.key, name: column.name }
  // A subtotal column always carries its own pack id (useCashPositionSummaryMultiPeriod).
  const packId = column.packId
  if (packId === null) throw new Error(`Pack column ${column.key} has no pack id`)
  return {
    kind: 'pack',
    id: packId,
    name: column.name,
    members: columns
      .filter((c) => c.kind === 'facility' && c.packId === packId)
      .map(({ key, name }) => ({ id: key, name })),
  }
}

/** Facility ids the cell sums, sorted: they are part of the cell's query key. */
export function cellFacilityIds(target: ProjectionCellTarget): string[] {
  return cellFacilities(target)
    .map((facility) => facility.id)
    .sort()
}

/** Stable key of a target: the facility id, or `pack:<id>`, so a pack never collides with a facility. */
export function cellTargetKey(target: ProjectionCellTarget): string {
  return target.kind === 'pack' ? `pack:${target.id}` : target.id
}

export function cellCategoryNames(row: ProjectionCellRow): readonly ReportCategoryName[] {
  return row.kind === 'outflows' ? OUTFLOW_CATEGORY_NAMES : [row.name]
}

/** The row label the report shows for `row` — the modal's category pill. */
export function cellRowLabel(row: ProjectionCellRow): string {
  if (row.kind === 'outflows') return 'Projected Outflows'
  // The report's DEPOSIT row is titled by what it sums, not by the category.
  return row.name === 'DEPOSIT' ? 'Projected Inflows' : REPORT_CATEGORY_LABEL[row.name]
}

/**
 * The tenant's category ids for the cell, matched by EXACT name like the report
 * SQL; a missing name is skipped (ADR 0005), so the result can be empty.
 */
export function resolveCellCategoryIds(
  categories: ReadonlyArray<{ id: string; name: string }>,
  row: ProjectionCellRow,
): string[] {
  const names = new Set<string>(cellCategoryNames(row))
  // Sorted: the ids are part of the cell's query key, and the categories list
  // order is not deterministic (seeded rows share `created_at`).
  return categories
    .filter((c) => names.has(c.name))
    .map((c) => c.id)
    .sort()
}

/**
 * Whether `date` is outside the cell's period — the only way a row written from
 * the modal leaves the cell (`include_in_report` and the category set are fixed
 * there, and a facility can only change to another of the cell's facilities).
 */
export function isOutsidePeriod(date: string, scope: ProjectionCellScope): boolean {
  return date < scope.start || date > scope.end
}

export interface CategoryOption {
  id: string
  label: string
}

/** Projected Outflows editor options: the outflow categories the tenant has, in report order. */
export function outflowCategoryOptions(
  categories: ReadonlyArray<{ id: string; name: string }>,
): CategoryOption[] {
  return OUTFLOW_CATEGORY_NAMES.flatMap((name) => {
    const category = categories.find((c) => c.name === name)
    return category ? [{ id: category.id, label: REPORT_CATEGORY_LABEL[name] }] : []
  })
}

/** Report label for an outflow category, the stored name otherwise, `''` for an unknown category. */
export function outflowCategoryLabel(name: string | undefined): string {
  if (name === undefined) return ''
  return isOutflowCategoryName(name) ? REPORT_CATEGORY_LABEL[name] : name
}

/** Modal order: date ascending, then description, then id as a stable tiebreak. */
export function sortCellRows<T extends Pick<ProjectionRecord, 'id' | 'date' | 'description'>>(
  rows: readonly T[],
): T[] {
  return [...rows].sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      (a.description ?? '').localeCompare(b.description ?? '', undefined, { sensitivity: 'base' }) ||
      a.id.localeCompare(b.id),
  )
}

/**
 * One row's edit, in the inputs' own shapes: `date` ISO or `''` (DateField),
 * `facilityId` / `categoryId` `''` for none, `amount` `undefined` when empty and
 * `NaN` when unparseable (AmountInput).
 */
export interface ProjectionDraft {
  date: string
  facilityId: string
  categoryId: string
  description: string
  amount: number | undefined
}

/** Per-field validation messages; a missing key means the field is valid. */
export type DraftErrors = Partial<Record<keyof ProjectionDraft, string>>

/** The snapshot an edit is diffed against. */
export function draftFromRecord(record: ProjectionRecord): ProjectionDraft {
  return {
    date: record.date,
    facilityId: record.facility_id ?? '',
    categoryId: record.category_id,
    description: record.description ?? '',
    // Coerced so the diff compares numbers even if a NUMERIC ever arrives as a
    // string; for a number this is the identity.
    amount: Number(record.amount),
  }
}

/**
 * Validates a draft on Apply. Description is required for edited rows too (an old
 * row stored without one must get one). Zero is valid; the sign is not checked.
 */
export function validateDraft(draft: ProjectionDraft): DraftErrors {
  const errors: DraftErrors = {}
  if (draft.date === '') errors.date = 'Date is required'
  else if (!isCalendarDate(draft.date)) errors.date = 'Enter a valid date'
  if (draft.facilityId === '') errors.facilityId = 'Facility is required'
  if (draft.categoryId === '') errors.categoryId = 'Category is required'
  if (draft.description.trim() === '') errors.description = 'Description is required'
  if (draft.amount === undefined) errors.amount = 'Amount is required'
  else if (!Number.isFinite(draft.amount)) errors.amount = 'Enter a valid amount'
  return errors
}

export function isDraftValid(errors: DraftErrors): boolean {
  return Object.keys(errors).length === 0
}

// `facility_id` is a plain string: a valid draft always names a facility, the modal never clears it.
export type ProjectionPatch = Partial<
  Pick<ProjectionRecord, 'date' | 'category_id' | 'description' | 'amount'> & { facility_id: string }
>

/**
 * Only the fields of a valid `draft` that differ from `base` (`{}` = no change),
 * so a concurrent edit of another field is not overwritten.
 */
export function diffDraft(base: ProjectionDraft, draft: ProjectionDraft): ProjectionPatch {
  const patch: ProjectionPatch = {}
  if (draft.date !== base.date) patch.date = draft.date
  if (draft.facilityId !== base.facilityId) patch.facility_id = draft.facilityId
  if (draft.categoryId !== base.categoryId) patch.category_id = draft.categoryId
  const description = draft.description.trim()
  if (description !== base.description.trim()) patch.description = description
  if (draft.amount !== undefined && draft.amount !== base.amount) patch.amount = draft.amount
  return patch
}

/**
 * The usual sign of a category's amounts — hints only; amounts are stored as typed
 * (DCP-250). `either`: INTER CO LOAN / LINE OF CREDIT move both ways.
 */
export type AmountSignKind = 'outflow' | 'inflow' | 'either'

/** In the Projected Outflows cell every option is an outflow, so the hint applies before a pick. */
export function amountSignKind(row: ProjectionCellRow, categoryName: string | undefined): AmountSignKind {
  if (row.kind === 'outflows') return 'outflow'
  const name = categoryName ?? row.name
  if (name === 'DEPOSIT') return 'inflow'
  return isOutflowCategoryName(name) ? 'outflow' : 'either'
}

export function amountPlaceholder(kind: AmountSignKind): string {
  switch (kind) {
    case 'outflow':
      return '-0.00'
    case 'inflow':
      return '0.00'
    case 'either':
      return '-0.00 or 0.00'
    default: {
      const unreachable: never = kind
      return unreachable
    }
  }
}

/** Non-blocking warning for an amount with an unusual sign for its category; zero never warns. */
export function amountSignWarning(kind: AmountSignKind, amount: number | undefined): string | null {
  if (amount === undefined || !Number.isFinite(amount)) return null
  if (kind === 'outflow' && amount > 0) return 'Positive amount increases cash (e.g. a refund). Is this intended?'
  if (kind === 'inflow' && amount < 0) return 'Negative amount decreases cash. Is this intended?'
  return null
}

/**
 * The create payload for "Copy". Omits `file_import_id` — a hand-made copy must
 * not be deleted with the import; `created_by` comes from the create hook and
 * `is_recurring` keeps its default.
 */
export function duplicatePayload(record: ProjectionRecord): ProjectionFormData {
  return {
    category_id: record.category_id,
    facility_id: record.facility_id ?? undefined,
    date: record.date,
    account_name: record.account_name ?? undefined,
    transaction_type: record.transaction_type ?? undefined,
    description: record.description,
    amount: record.amount,
    status: record.status,
    notes: record.notes ?? undefined,
    attachment_url: record.attachment_url ?? undefined,
    attachment_name: record.attachment_name ?? undefined,
    priority: record.priority,
    include_in_report: record.include_in_report,
  }
}

export function formatProjectionCount(n: number): string {
  return `${n} projection${n === 1 ? '' : 's'}`
}
